import { Step } from './gemini';
import { isValidRenderableImageUrl } from './visualUtils';

/**
 * Strict Safe Teaching Area & Frame Layout Engine
 * 
 * CORE PRINCIPLES:
 * 1. STRICT BUDGET: Normally maximum 3–4 logical teaching STEPS per frame (fewer when required by measured space).
 * 2. LOGICAL UNIT INTEGRITY: A single logical teaching step can naturally contain multiple lines, equations, 
 *    definitions, or calculations. Pagination preserves complete logical teaching steps intact rather than 
 *    arbitrarily splitting by line count.
 * 3. REAL LAYOUT MEASUREMENT & SAFE BOUNDS: Strict physical boundary validation for rendered whiteboard content:
 *    left >= SAFE_LEFT, right <= SAFE_RIGHT, top >= SAFE_TOP, bottom <= SAFE_BOTTOM
 * 4. ATOMIC TOKEN COHESION: Equations, variables, and units that genuinely fit on one line remain on one line.
 * 5. STRUCTURED EDUCATIONAL REPRESENTATIONS: Intelligently handles matrices, tables, aligned derivations,
 *    and formulas with accurate bounding boxes.
 */

export interface SafeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

// 16:9 Landscape MP4 / Web Player (1920x1080) whiteboard safe teaching area
export const SAFE_BOUNDS_LANDSCAPE: SafeBounds = {
  left: 56,
  top: 36,
  right: 1140, // Left whiteboard width bounded before diagram panel
  bottom: 500, // Safe height above caption strip and reference footer
  width: 1084,
  height: 460
};

// 9:16 Portrait YouTube Shorts (1080x1920) whiteboard safe teaching area
export const SAFE_BOUNDS_SHORTS: SafeBounds = {
  left: 48,
  top: 240,
  right: 1032,
  bottom: 670, // Above bottom avatar & visual panels
  width: 984,
  height: 430
};

// Maximum and minimum logical teaching steps allowed in a single teaching frame
export const MAX_LOGICAL_STEPS_PER_FRAME = 4;
export const MIN_LOGICAL_STEPS_PER_FRAME = 1;
export const COMPLEX_STEP_MAX_PER_FRAME = 3;

/**
 * Represents a discrete, atomic logical teaching step.
 * Examples:
 * - A heading / problem statement (e.g., "Example Calculation: Let a = 3 units, b = 4 units")
 * - A formula or derivation step (e.g., "$3^2 + 4^2 = c^2 \implies c = 5\text{ units}$")
 * - A matrix or determinant block (e.g., "A = \begin{pmatrix} 1 & 2 \\ 3 & 4 \end{pmatrix}")
 * - A structured data table (e.g., "| Time (s) | Distance (m) | ...")
 * - An aligned derivation block (e.g., "\begin{aligned} ... \end{aligned}")
 * - A parameter definition bullet (e.g., "• where $w$ = river width and $\theta$ = angle with perpendicular")
 * - A given data item (e.g., "• Rocket Acceleration $(a_r) = 2\text{ m/s}^2\ (+x)$")
 */
export interface LogicalTeachingStep {
  id: string;
  type: 'heading' | 'bullet' | 'equation' | 'matrix' | 'aligned' | 'table' | 'statement' | 'chart';
  rawText: string;
  lines: string[];
  isComplex: boolean;
}

/**
 * Checks whether a line is a genuine Markdown table row vs a mathematical equation containing absolute value/determinant bars.
 */
export function isRealMarkdownTableRow(line: string): boolean {
  if (!line) return false;
  const trimmed = line.trim();

  // If it's a LaTeX environment or starts with solve/where/let, it's not a table
  if (trimmed.includes('\\begin{') || trimmed.includes('\\end{')) return false;
  if (/^(Solve|Where|Let|Finding|Given|Step|Objective|Calculate)\b/i.test(trimmed)) return false;
  if (trimmed.includes('=') && (trimmed.includes('\\lambda') || trimmed.includes('\\det') || trimmed.includes('\\cdot') || trimmed.includes('\\times'))) return false;

  // If pipes are enclosed in single-dollar or double-dollar math delimiters like $|A - \lambda I| = 0$
  if (/\$[^$]*\|[^$]*\|[^$]*\$/.test(trimmed) || /\|[^|]+\|\s*=\s*\d+/.test(trimmed)) return false;

  // Markdown table separator row |---|---|
  if (/^\|?(\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?$/.test(trimmed)) return true;

  // Standard markdown table row starting and ending with | with content in between
  if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
    const pipeCount = (trimmed.match(/\|/g) || []).length;
    return pipeCount >= 2;
  }

  // Two or more pipes separating text (e.g. Item | Value | Unit)
  const pipeCount = (trimmed.match(/\|/g) || []).length;
  if (pipeCount >= 2 && !trimmed.includes('$') && !trimmed.includes('\\')) {
    return true;
  }

  return false;
}

/**
 * Cleans and repairs broken LaTeX/text lines generated by AI or split inappropriately.
 */
export function cleanMathAndTextLine(raw: string): string {
  if (!raw) return '';
  let line = raw.trim();

  // 1. Remove accidental Markdown code fences
  line = line.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '').trim();

  // 2. Fix corrupted word splits caused by previous regexes
  line = line.replace(/\bIn\s+for\s+mation\b/gi, 'Information');
  line = line.replace(/\bIn\s+formation\b/gi, 'Information');
  line = line.replace(/\buni\s+form\b/gi, 'uniform');
  line = line.replace(/\btrans\s+for\s+mation\b/gi, 'transformation');
  line = line.replace(/\bthere\s+for\s+e\b/gi, 'therefore');

  // 3. Fix corrupted matrix/determinant environment names
  line = line.replace(/\\begin\s*\{\s*p\s*matrix\s*\}/gi, '\\begin{pmatrix}');
  line = line.replace(/\\end\s*\{\s*p\s*matrix\s*\}/gi, '\\end{pmatrix}');
  line = line.replace(/\\begin\s*\{\s*v\s*matrix\s*\}/gi, '\\begin{vmatrix}');
  line = line.replace(/\\end\s*\{\s*v\s*matrix\s*\}/gi, '\\end{vmatrix}');
  line = line.replace(/\\begin\s*\{\s*b\s*matrix\s*\}/gi, '\\begin{bmatrix}');
  line = line.replace(/\\end\s*\{\s*b\s*matrix\s*\}/gi, '\\end{bmatrix}');
  line = line.replace(/\\begin\s*\{\s*matrix\s*\}/gi, '\\begin{matrix}');
  line = line.replace(/\\end\s*\{\s*matrix\s*\}/gi, '\\end{matrix}');
  line = line.replace(/\\begin\s*\{\s*aligned\s*\}/gi, '\\begin{aligned}');
  line = line.replace(/\\end\s*\{\s*aligned\s*\}/gi, '\\end{aligned}');
  line = line.replace(/\\begin\s*\{\s*cases\s*\}/gi, '\\begin{cases}');
  line = line.replace(/\\end\s*\{\s*cases\s*\}/gi, '\\end{cases}');

  // 4. Normalize and fix common LaTeX escaping errors
  line = line.replace(/\f/g, '\\frac').replace(/\t/g, '\\tau');
  line = line.replace(/\\b(?![a-zA-Z])/g, '').replace(/[\b]/g, '');

  // 5. Clean up markdown heading markers at the beginning of content lines so they don't produce raw '#'
  line = line.replace(/^#{1,6}\s+/, '');

  // 6. Clean isolated bracket lines and orphaned variable fragments
  line = line.replace(/\(\s*\$([a-zA-Z0-9_\^\\\{\}]+)\$\s*\)/g, '($1)');
  line = line.replace(/\s{2,}/g, ' ').trim();

  // Normalize subscripts in variable definitions: (n1) -> (n_1), (n2) -> (n_2), (N1) -> (N_1)
  line = line.replace(/\(([a-zA-Z])(\d+)\)/g, '($1_$2)');

  // 7. Ensure inline units are properly formatted with space and \text{}
  line = line.replace(/([=+\-\*\/]|\b(?:approx|sim|pm)\b|\\approx|\\sim|\\pm)\s*([+-]?\d+([.,]\d+)?)\s*(mm|cm|km|km\/h|m\/s\^2|m\/s|kg|g|mg|sec|seconds|Hz|kHz|MHz|kN|kJ|kW|kPa|mV|mA|μF|rad|deg|degrees|units)\b/g, '$1 $2\\text{ $4}');
  line = line.replace(/\\sqrt\{(\d+)\}\s*=\s*(\d+)\s*(mm|cm|m|km|seconds|kg|units)\b/g, '\\sqrt{$1} = $2\\text{ $3}');

  return line;
}

/**
 * Pre-processes full board content into normalized, logically grouped lines.
 * Recombines erroneously split definitions, formulas, and orphan lines while preserving matrices and tables.
 */
export function normalizeBoardContentLines(rawContent: string): string[] {
  if (!rawContent) return [];

  let content = rawContent
    .replace(/\\+\[\s*\d*\.?\d*\s*e\s*m\s*\]/g, ' ')
    .replace(/\[BR\]/gi, '\n');

  // If a line begins with a section heading label followed by data (e.g. "GIVEN DATA : REFRACTIVE INDEX OF AIR (N1) = 1.0"),
  // split it into the Heading line and the Bullet/Data line so they do not concatenate
  content = content.replace(/^([•\s]*(?:GIVEN DATA|OBJECTIVE|STEP \d+|EXAMPLE CALCULATION|FORMULA|KEY CONCEPT))\s*:\s*(.+)$/gim, (m, p1, p2) => {
    const cleanHeader = p1.replace(/^•\s*/, '').trim();
    if (p2 && p2.trim().length > 0 && (p2.includes('=') || /[a-zA-Z]{3,}/.test(p2))) {
      return `${cleanHeader}:\n• ${p2.trim()}`;
    }
    return m;
  });

  // Extract and protect complete LaTeX environments (e.g. \begin{pmatrix}...\end{pmatrix}) so their internal '\\' row breaks aren't destroyed
  const protectedEnvs: string[] = [];
  content = content.replace(/\\begin\{(pmatrix|bmatrix|vmatrix|matrix|aligned|cases|split)\}[\s\S]*?\\end\{\1\}/gi, (match) => {
    protectedEnvs.push(match);
    return `___LATEX_ENV_${protectedEnvs.length - 1}___`;
  });

  // Only split explicit non-matrix newlines
  const rawLines = content.split('\n').map(l => l.trim()).filter(Boolean);
  if (rawLines.length === 0 && protectedEnvs.length === 0) return [];

  const mergedLines: string[] = [];
  let currentAccumulator = '';

  for (let i = 0; i < rawLines.length; i++) {
    let line = rawLines[i];
    
    // Restore protected environment if present
    line = line.replace(/___LATEX_ENV_(\d+)___/g, (_, idx) => protectedEnvs[Number(idx)] || '');

    const cleaned = cleanMathAndTextLine(line);
    if (!cleaned) continue;

    // Do NOT merge table rows into other lines
    const isTableRow = isRealMarkdownTableRow(cleaned);
    if (isTableRow) {
      if (currentAccumulator) {
        mergedLines.push(currentAccumulator);
        currentAccumulator = '';
      }
      mergedLines.push(cleaned);
      continue;
    }

    // Do NOT merge standalone matrix/aligned environments into text lines
    if (cleaned.includes('\\begin{')) {
      if (currentAccumulator) {
        mergedLines.push(currentAccumulator);
        currentAccumulator = '';
      }
      mergedLines.push(cleaned);
      continue;
    }

    // Check if this line is an orphaned fragment (e.g. "units", ") =", "$a_r$", "(", "+x", ")")
    const isOrphanFragment = 
      /^(units|meters|seconds|kg|m\/s|m\/s\^2|\)|=\s*\S+|\(\s*\S*\s*\)|\+\s*\S+)$/i.test(cleaned) ||
      /^(\([a-zA-Z0-9_\s]*\)|[=+\-*\/]\s*.*)$/.test(cleaned) ||
      (cleaned.length <= 4 && !/^[•\d\-]/.test(cleaned) && !cleaned.includes('$') && !cleaned.includes('\\'));

    // Check if previous accumulator is a heading or bullet (headings/bullets must NEVER swallow subsequent equations)
    const isAccumulatorHeadingOrBullet = currentAccumulator && (
      /^(GIVEN DATA|TO FIND|TO PROVE|GOAL|OBJECTIVE|WHAT TO FIND|WHAT TO PROVE|STEP \d+|EXAMPLE CALCULATION|FORMULA|KEY CONCEPT|CONCLUSION|METHOD|CASE \d+|DEFINITION|FINDING)/i.test(currentAccumulator) ||
      currentAccumulator.endsWith(':') ||
      currentAccumulator.startsWith('•') ||
      currentAccumulator.startsWith('$$')
    );

    // Check if the previous line ended with an open parenthetical or incomplete operator (excluding headings)
    const prevEndsIncomplete = currentAccumulator && !isAccumulatorHeadingOrBullet && /([(=+\-*\/]|where|Let|and|with)\s*$/i.test(currentAccumulator);

    if (currentAccumulator && (isOrphanFragment || prevEndsIncomplete)) {
      currentAccumulator = `${currentAccumulator} ${cleaned}`.replace(/\s{2,}/g, ' ');
    } else {
      if (currentAccumulator) {
        mergedLines.push(currentAccumulator);
      }
      currentAccumulator = cleaned;
    }
  }

  if (currentAccumulator) {
    mergedLines.push(currentAccumulator);
  }

  // Format bullet points consistently
  return mergedLines.map(l => {
    let formatted = l.trim();
    const isTable = isRealMarkdownTableRow(formatted);
    if (isTable || formatted.includes('\\begin{')) return formatted;

    if (!formatted.startsWith('•') && !formatted.startsWith('*') && !/^\d+\./.test(formatted) && !formatted.startsWith('$$')) {
      const isHeading = /^(GIVEN DATA|OBJECTIVE|STEP \d+|EXAMPLE CALCULATION|FORMULA|KEY CONCEPT|CONCLUSION|METHOD|CASE \d+|DEFINITION|FINDING)/i.test(formatted) || formatted.endsWith(':');
      const isStandaloneEquation = /^[a-zA-Z0-9_\^\{\}\\\+\-\*\/\=\(\)\s]+$/.test(formatted) && formatted.includes('=') && !formatted.includes('Let') && !formatted.includes('where');
      
      if (!isHeading && !isStandaloneEquation && formatted.length > 20) {
        formatted = `• ${formatted}`;
      }
    }
    return formatted;
  });
}

/**
 * Parses raw board content into structured Logical Teaching Steps.
 * A single logical teaching step may contain a complete multi-line table, a matrix, or an aligned derivation.
 */
export function extractLogicalTeachingSteps(rawContent: string): LogicalTeachingStep[] {
  const lines = normalizeBoardContentLines(rawContent);
  if (lines.length === 0) return [];

  const steps: LogicalTeachingStep[] = [];
  let currentGroup: string[] = [];
  let currentType: LogicalTeachingStep['type'] = 'statement';

  const flushCurrent = () => {
    if (currentGroup.length === 0) return;
    const fullText = currentGroup.join('\n');
    const isComplex = /\\begin\{(vmatrix|bmatrix|pmatrix|matrix|aligned|cases)\}|\\frac\{.*\}\{.*\}|\|/.test(fullText);
    steps.push({
      id: `step-${steps.length + 1}`,
      type: currentType,
      rawText: fullText,
      lines: [...currentGroup],
      isComplex
    });
    currentGroup = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isTable = isRealMarkdownTableRow(line);
    const isHeading = /^(GIVEN DATA|TO FIND|TO PROVE|GOAL|OBJECTIVE|WHAT TO FIND|WHAT TO PROVE|STEP \d+|EXAMPLE CALCULATION|FORMULA|KEY CONCEPT|CONCLUSION|METHOD|CASE \d+|DEFINITION|FINDING)/i.test(line) || line.endsWith(':');
    const isBullet = /^([-*•]|\d+\.)\s+/.test(line);
    const isMatrix = /\\begin\{(pmatrix|bmatrix|vmatrix|matrix)\}/i.test(line);
    const isAligned = /\\begin\{(aligned|cases|split)\}/i.test(line);
    const isBlockMath = line.startsWith('$$');

    // 1. Table handling (group all contiguous table rows together)
    if (isTable) {
      if (currentType === 'table') {
        currentGroup.push(line);
      } else {
        flushCurrent();
        currentType = 'table';
        currentGroup = [line];
      }
      continue;
    }

    // 2. Matrix handling
    if (isMatrix) {
      flushCurrent();
      currentType = 'matrix';
      currentGroup = [line];
      flushCurrent();
      continue;
    }

    // 3. Aligned equation handling
    if (isAligned) {
      flushCurrent();
      currentType = 'aligned';
      currentGroup = [line];
      flushCurrent();
      continue;
    }

    // 4. Heading handling - each heading is flushed immediately as its own single-line step
    if (isHeading) {
      flushCurrent();
      currentType = 'heading';
      currentGroup = [line];
      flushCurrent();
      continue;
    }

    // 5. Standalone Equation handling - display equations or fraction equations are independent logical steps
    const isStandaloneEquation = isBlockMath || /\\frac\{|\\sum|\\int|\\prod/.test(line) || (/=/.test(line) && !line.startsWith('•') && !/^(where|let|and|with)\b/i.test(line));
    if (isStandaloneEquation) {
      flushCurrent();
      currentType = 'equation';
      currentGroup = [line];
      flushCurrent();
      continue;
    }

    // 6. Bullet handling
    if (isBullet) {
      flushCurrent();
      currentType = 'bullet';
      currentGroup = [line];
      continue;
    }

    // 7. Continuing prose statement
    if (currentGroup.length === 0) {
      currentType = 'statement';
      currentGroup = [line];
    } else {
      if (currentType === 'bullet' && !line.startsWith('•') && !line.includes('=')) {
        currentGroup.push(line);
      } else {
        flushCurrent();
        currentType = 'statement';
        currentGroup = [line];
      }
    }
  }

  flushCurrent();
  return steps;
}

/**
 * Calculates the exact rendered pixel height requirement of a logical teaching step.
 * Accurately measures tables, matrices, aligned equations, KaTeX blocks, and line wraps.
 */
export function measureLogicalStepHeight(
  step: LogicalTeachingStep,
  containerWidth: number = SAFE_BOUNDS_LANDSCAPE.width,
  fontSizePx: number = 32
): number {
  // HEADING MEASUREMENT
  if (step.type === 'heading') {
    return 56;
  }

  // TABLE MEASUREMENT
  if (step.type === 'table') {
    const rowCount = step.lines.filter(l => !/^[|\-:\s]+$/.test(l)).length;
    const headerHeight = fontSizePx * 1.6 + 20;
    const dataRowHeight = fontSizePx * 1.4 + 16;
    return headerHeight + Math.max(1, rowCount - 1) * dataRowHeight + 32;
  }

  // MATRIX MEASUREMENT
  if (step.type === 'matrix') {
    const raw = step.rawText;
    const rowCount = (raw.match(/\\\\/g) || []).length + 1;
    return rowCount * (fontSizePx * 1.8) + 40;
  }

  // ALIGNED DERIVATION MEASUREMENT
  if (step.type === 'aligned') {
    const raw = step.rawText;
    const rowCount = (raw.match(/\\\\/g) || []).length + 1;
    return rowCount * (fontSizePx * 1.6) + 36;
  }

  // EQUATION MEASUREMENT (with KaTeX display margins and line spacing)
  if (step.type === 'equation') {
    const raw = step.rawText;
    const hasFractions = /\\frac\{/.test(raw);
    const hasTallOperators = /\\sum|\\int|\\prod|\\sqrt/.test(raw);
    if (hasFractions || hasTallOperators) {
      return 115;
    }
    return 65;
  }

  const approxCharsPerLine = Math.max(30, Math.floor(containerWidth / (fontSizePx * 0.55)));
  let totalHeight = 0;

  for (const line of step.lines) {
    const len = line.length;
    const lineWraps = Math.max(1, Math.ceil(len / approxCharsPerLine));
    let lineHeight = lineWraps * (fontSizePx * 1.4);

    // KaTeX tall fraction or integral/sum within bullet
    if (/\\frac\{/.test(line) || /\\sum|\\int|\\prod/.test(line)) {
      lineHeight += fontSizePx * 1.2;
    }

    totalHeight += lineHeight + 14;
  }

  return totalHeight + 12;
}

/**
 * Measures the complete rendered height of a set of logical steps in a frame.
 */
export function measureFrameTotalHeight(
  steps: LogicalTeachingStep[],
  containerWidth: number = SAFE_BOUNDS_LANDSCAPE.width,
  fontSizePx: number = 32
): number {
  return steps.reduce((acc, s) => acc + measureLogicalStepHeight(s, containerWidth, fontSizePx), 0);
}

/**
 * Validates whether a rendered DOM element is fully within safe bounds rectangle.
 */
export function isWithinSafeBounds(rect: DOMRect, safe: SafeBounds, margin: number = 4): boolean {
  return (
    rect.left >= safe.left - margin &&
    rect.top >= safe.top - margin &&
    rect.right <= safe.right + margin &&
    rect.bottom <= safe.bottom + margin
  );
}

/**
 * Client-side layout validator: checks all teaching content elements inside container.
 * Returns true if all elements strictly satisfy the safe bounding box.
 */
export function validateRenderedFrameBounds(
  containerEl: HTMLElement | null,
  safe: SafeBounds = SAFE_BOUNDS_LANDSCAPE
): { isValid: boolean; overflowPx: number } {
  if (!containerEl) return { isValid: true, overflowPx: 0 };

  const children = Array.from(containerEl.children) as HTMLElement[];

  let maxOverflow = 0;
  for (const child of children) {
    const rect = child.getBoundingClientRect();
    if (rect.bottom > safe.bottom) {
      maxOverflow = Math.max(maxOverflow, rect.bottom - safe.bottom);
    }
    if (rect.right > safe.right) {
      maxOverflow = Math.max(maxOverflow, rect.right - safe.right);
    }
    if (rect.top < safe.top) {
      maxOverflow = Math.max(maxOverflow, safe.top - rect.top);
    }
    if (rect.left < safe.left) {
      maxOverflow = Math.max(maxOverflow, safe.left - rect.left);
    }
  }

  return {
    isValid: maxOverflow <= 2,
    overflowPx: maxOverflow
  };
}

/**
 * Smart Semantic & Pedagogical Text-Narration Synchronizer.
 * Matches spoken lecture narration sentences to sequential teaching frames using keyword affinity
 * and strictly monotonic chronological progression.
 */
export function alignSentencesToTeachingFrames(
  textFramesData: LogicalTeachingStep[][],
  fullExp: string,
  fullAudio: string
): { frameExplanations: string[]; frameAudios: string[] } {
  const K = textFramesData.length;
  if (K === 0) return { frameExplanations: [], frameAudios: [] };
  if (K === 1) return { frameExplanations: [fullExp], frameAudios: [fullAudio] };

  // Split into sentences
  const expSentences = (fullExp || '').split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);
  const audioSentences = (fullAudio || fullExp || '').split(/(?<=[.!?।॥؟。])\s+/).filter(s => s.trim().length > 0);

  const M_exp = expSentences.length;
  const M_aud = audioSentences.length;

  if (M_exp <= 1 && M_aud <= 1) {
    return {
      frameExplanations: textFramesData.map(() => fullExp),
      frameAudios: textFramesData.map(() => fullAudio)
    };
  }

  // 1. Extract significant keywords/tokens from each frame
  const frameKeywords: Set<string>[] = textFramesData.map(group => {
    const raw = group.map(g => g.rawText).join(' ').toLowerCase();
    // Remove math symbols / delimiters and split into clean alphanumeric tokens
    const tokens = raw.replace(/[\$\\_{}\[\]\(\)\^=+\-*/|#,:;"]/g, ' ').split(/\s+/).filter(t => t.length > 2);
    // Remove common stopwords
    const stopwords = new Set([
      'the', 'and', 'for', 'with', 'that', 'this', 'from', 'are', 'was', 'were', 'have', 'has', 'had',
      'what', 'which', 'when', 'where', 'step', 'case', 'value', 'data', 'note', 'will', 'then', 'into'
    ]);
    return new Set(tokens.filter(t => !stopwords.has(t)));
  });

  // 2. If M_exp >= K, find optimal monotonic sentence split boundaries 0 = b_0 < b_1 < ... < b_K = M_exp
  let expBoundaries: number[] = Array(K + 1).fill(0);
  expBoundaries[K] = M_exp;

  if (M_exp >= K) {
    const dp: number[][] = Array(K + 1).fill(0).map(() => Array(M_exp + 1).fill(-Infinity));
    const parent: number[][] = Array(K + 1).fill(0).map(() => Array(M_exp + 1).fill(0));

    dp[0][0] = 0;

    for (let k = 1; k <= K; k++) {
      const targetKeywords = frameKeywords[k - 1];
      const remainingFramesAfter = K - k;

      for (let m = k; m <= M_exp - remainingFramesAfter; m++) {
        for (let prev = k - 1; prev < m; prev++) {
          if (dp[k - 1][prev] === -Infinity) continue;

          const slice = expSentences.slice(prev, m);
          let score = 0;
          let wordCount = 0;

          for (const sent of slice) {
            const sentWords = sent.toLowerCase().split(/\s+/).filter(Boolean);
            wordCount += sentWords.length;
            for (const w of sentWords) {
              const cleanW = w.replace(/[^a-z0-9]/g, '');
              if (cleanW && targetKeywords.has(cleanW)) {
                score += 18; // Strong semantic keyword match
              }
            }
          }

          // Reward balanced length (~25-65 words per frame)
          if (wordCount >= 20 && wordCount <= 75) {
            score += 12;
          } else if (wordCount > 90) {
            score -= (wordCount - 90) * 0.4;
          }

          // Positional progression bias (early frames get early sentences)
          const expectedCenter = ((k - 0.5) / K) * M_exp;
          const actualCenter = (prev + m - 1) / 2;
          const dist = Math.abs(expectedCenter - actualCenter);
          score += Math.max(0, 10 - dist * 2);

          const total = dp[k - 1][prev] + score;
          if (total > dp[k][m]) {
            dp[k][m] = total;
            parent[k][m] = prev;
          }
        }
      }
    }

    let currM = M_exp;
    for (let k = K; k >= 1; k--) {
      const prev = parent[k][currM] !== undefined ? parent[k][currM] : (k - 1);
      expBoundaries[k - 1] = prev;
      currM = prev;
    }
  } else {
    // If fewer sentences than frames, map sentences monotonically and duplicate for remaining
    for (let k = 0; k <= K; k++) {
      expBoundaries[k] = Math.min(M_exp, Math.floor((k / K) * M_exp));
    }
  }

  // Ensure strict monotonicity 0 = b_0 <= b_1 <= ... <= b_K = M_exp
  for (let k = 1; k < K; k++) {
    if (expBoundaries[k] < expBoundaries[k - 1]) {
      expBoundaries[k] = expBoundaries[k - 1];
    }
    if (expBoundaries[k] > M_exp) {
      expBoundaries[k] = M_exp;
    }
  }
  expBoundaries[K] = M_exp;

  // Build audio boundaries matching proportional progress
  const audBoundaries: number[] = Array(K + 1).fill(0);
  audBoundaries[0] = 0;
  audBoundaries[K] = M_aud;
  for (let k = 1; k < K; k++) {
    const ratio = M_exp > 0 ? expBoundaries[k] / M_exp : k / K;
    audBoundaries[k] = Math.min(M_aud, Math.max(audBoundaries[k - 1], Math.round(ratio * M_aud)));
  }

  const frameExplanations: string[] = [];
  const frameAudios: string[] = [];

  for (let k = 0; k < K; k++) {
    const eStart = expBoundaries[k];
    const eEnd = expBoundaries[k + 1];
    const expSlice = expSentences.slice(eStart, eEnd);
    let expStr = expSlice.length > 0 ? expSlice.join(' ').trim() : expSentences[Math.min(k, M_exp - 1)] || fullExp;

    const aStart = audBoundaries[k];
    const aEnd = audBoundaries[k + 1];
    const audSlice = audioSentences.slice(aStart, aEnd);
    let audStr = audSlice.length > 0 ? audSlice.join(' ').trim() : audioSentences[Math.min(k, M_aud - 1)] || fullAudio || expStr;

    frameExplanations.push(expStr);
    frameAudios.push(audStr);
  }

  return { frameExplanations, frameAudios };
}

/**
 * Splits explanation and audio explanation between textual teaching frames and the dedicated visual frame.
 */
export function splitExplanationAndAudioForVisual(
  explanation: string,
  audioExplanation: string,
  diagramDescription?: string
): { textExp: string; textAudio: string; visualExp: string; visualAudio: string } {
  const expText = (explanation || '').trim();
  const audioText = (audioExplanation || explanation || '').trim();
  const defaultVisualText = diagramDescription?.trim() || 'Let us observe the visual diagram illustrating these principles.';

  const visualKeywords = /\b(diagram|figure|visual|graph|plot|coordinate|illustrated|illustration|observe|shown in|see the|looking at|look at|notice the|axis|axes|geometry|triangle|circle|vector|sketch|representation|free body|curve|wave|string|pulse|plane|slope)\b/i;

  const expSentences = expText.split(/(?<=[.!?])\s+/).filter(Boolean);
  const audioSentences = audioText.split(/(?<=[.!?])\s+/).filter(Boolean);

  let textExp = expText;
  let visualExp = defaultVisualText;
  let textAudio = audioText;
  let visualAudio = defaultVisualText;

  if (expSentences.length > 1) {
    const vIndices = expSentences
      .map((st, idx) => ({ st, idx, isV: visualKeywords.test(st) }))
      .filter(i => i.isV)
      .map(i => i.idx);

    if (vIndices.length > 0 && vIndices.length < expSentences.length) {
      textExp = expSentences.filter((_, idx) => !vIndices.includes(idx)).join(' ');
      visualExp = expSentences.filter((_, idx) => vIndices.includes(idx)).join(' ');
    } else {
      const splitPt = Math.max(1, expSentences.length - 1);
      textExp = expSentences.slice(0, splitPt).join(' ');
      visualExp = expSentences.slice(splitPt).join(' ');
    }
  }

  if (audioSentences.length > 1) {
    const vAudioIndices = audioSentences
      .map((st, idx) => ({ st, idx, isV: visualKeywords.test(st) }))
      .filter(i => i.isV)
      .map(i => i.idx);

    if (vAudioIndices.length > 0 && vAudioIndices.length < audioSentences.length) {
      textAudio = audioSentences.filter((_, idx) => !vAudioIndices.includes(idx)).join(' ');
      visualAudio = audioSentences.filter((_, idx) => vAudioIndices.includes(idx)).join(' ');
    } else {
      const splitPt = Math.max(1, audioSentences.length - 1);
      textAudio = audioSentences.slice(0, splitPt).join(' ');
      visualAudio = audioSentences.slice(splitPt).join(' ');
    }
  }

  return { textExp, textAudio, visualExp, visualAudio };
}

/**
 * Intelligently packages a lesson Step into overflow-proof, narration-aligned teaching frames.
 * 
 * STRICT FRAME SEQUENCE RULES:
 * 1. FRAME 1 — MAIN TEACHING / TEXT FRAME: Contains only the textual teaching content/solution steps.
 *    The diagram/visual is NOT shown in this frame.
 * 2. FRAME 2 — DEDICATED VISUAL / DIAGRAM FRAME: When a visual is required, it receives its own clean frame.
 *    The textual solution steps from Frame 1 are NOT repeated or displayed in this visual frame.
 * 3. PEDAGOGICAL NARRATION SYNCHRONIZATION:
 *    - Long multi-concept steps are partitioned into focused narration-aligned frames (1-2 points per frame).
 *    - Spoken narration transitions smoothly with displayed content.
 * 4. BUDGET & OVERFLOW: Maximum 3–4 logical teaching steps per text frame, preserving complete units.
 */
export function paginateSingleStep(
  step: Step,
  stepIndex: number,
  safeBounds: SafeBounds = SAFE_BOUNDS_LANDSCAPE
): Step[] {
  if (!step) return [];

  const rawBoard = (step.board_content || '').trim();
  const logicalSteps = extractLogicalTeachingSteps(rawBoard);

  const stepValidImg = isValidRenderableImageUrl((step as any).image_url) 
    ? (step as any).image_url 
    : (isValidRenderableImageUrl((step as any).static_image_url) ? (step as any).static_image_url : undefined);

  const hasDedicatedVisual = Boolean(
    (step.diagram_svg && step.diagram_svg.toLowerCase().includes('<svg')) ||
    stepValidImg
  );

  const hasTextualSteps = logicalSteps.length > 0 && !(logicalSteps.length === 1 && logicalSteps[0].type === 'heading' && logicalSteps[0].rawText.length < 30);

  // Case 1: Pure Visual Frame (no textual teaching steps)
  if (hasDedicatedVisual && !hasTextualSteps) {
    return [{
      ...step,
      step: stepIndex,
      board_content: '',
      explanation: step.explanation || step.diagram_description || 'Visualizing concept...',
      audio_explanation: step.audio_explanation || step.diagram_description || 'Visualizing concept...',
      diagram_svg: step.diagram_svg,
      diagram_description: step.diagram_description,
      image_url: stepValidImg,
      static_image_url: stepValidImg,
      reference_text: step.reference_text
    }];
  }

  // Case 2: No textual steps and no visual (empty step fallback)
  if (logicalSteps.length === 0) {
    return [step];
  }

  // Split explanation & spoken narration if this step has both text and a dedicated visual
  const { textExp, textAudio, visualExp, visualAudio } = hasDedicatedVisual
    ? splitExplanationAndAudioForVisual(step.explanation || '', step.audio_explanation || '', step.diagram_description)
    : {
        textExp: step.explanation || '',
        textAudio: step.audio_explanation || step.explanation || '',
        visualExp: '',
        visualAudio: ''
      };

  const baseFontSize = safeBounds.width > 1000 ? 32 : 28;
  const hasComplex = logicalSteps.some(s => s.isComplex || s.type === 'table' || s.type === 'matrix' || s.type === 'aligned');
  const frameLimit = hasComplex ? COMPLEX_STEP_MAX_PER_FRAME : MAX_LOGICAL_STEPS_PER_FRAME;

  const audioWords = (textAudio || textExp || '').trim().split(/\s+/).filter(Boolean).length;
  const totalMeasuredHeight = measureFrameTotalHeight(logicalSteps, safeBounds.width, baseFontSize);

  // Reserve safe clearance for reference text badge and chalkboard vertical padding
  const hasRefText = Boolean(step.reference_text && step.reference_text.trim());
  const effectiveSafeHeight = safeBounds.height - (hasRefText ? 65 : 0) - 36;

  let textFramesData: LogicalTeachingStep[][] = [];

  // Determine if this step should be partitioned into granular narration-aligned teaching frames
  // A step with multiple distinct teaching bullets/concepts and substantial narration (>50 words or >1 sentence per bullet)
  // should progress synchronously with the narration rather than remaining static.
  const shouldSegmentByNarration = 
    logicalSteps.length >= 2 && 
    (audioWords >= 45 || audioWords >= logicalSteps.length * 25);

  if (!shouldSegmentByNarration && logicalSteps.length <= frameLimit && totalMeasuredHeight <= effectiveSafeHeight) {
    // Concise single-concept step fits safely within 1 frame
    textFramesData = [logicalSteps];
  } else if (shouldSegmentByNarration) {
    // Group logical steps into focused pedagogical frames (1-2 points per frame)
    let currentFrameSteps: LogicalTeachingStep[] = [];
    let currentFrameHeight = 0;

    for (let i = 0; i < logicalSteps.length; i++) {
      const lStep = logicalSteps[i];
      const stepHeight = measureLogicalStepHeight(lStep, safeBounds.width, baseFontSize);

      // If frame starts with a Heading, attach up to 2 items to that heading in the same frame
      const hasHeading = currentFrameSteps.length > 0 && currentFrameSteps[0].type === 'heading';
      const frameBudget = hasHeading ? 3 : (hasComplex ? 2 : 3);
      const countComplex = currentFrameSteps.filter(s => s.isComplex || s.type === 'equation').length;
      const wouldExceedComplex = (lStep.isComplex || lStep.type === 'equation') && countComplex >= 2 && currentFrameSteps.length >= 2;

      const wouldExceedPedagogicalBudget = currentFrameSteps.length >= frameBudget;
      const wouldExceedHeight = (currentFrameHeight + stepHeight) > effectiveSafeHeight && currentFrameSteps.length >= 1;

      if (currentFrameSteps.length > 0 && (wouldExceedPedagogicalBudget || wouldExceedHeight || wouldExceedComplex)) {
        textFramesData.push(currentFrameSteps);
        currentFrameSteps = [lStep];
        currentFrameHeight = stepHeight;
      } else {
        currentFrameSteps.push(lStep);
        currentFrameHeight += stepHeight;
      }
    }

    if (currentFrameSteps.length > 0) {
      textFramesData.push(currentFrameSteps);
    }
  } else {
    // Physical bounds partitioning for large tables or overflow
    let currentFrameSteps: LogicalTeachingStep[] = [];
    let currentFrameHeight = 0;

    for (let i = 0; i < logicalSteps.length; i++) {
      const lStep = logicalSteps[i];
      const stepHeight = measureLogicalStepHeight(lStep, safeBounds.width, baseFontSize);

      const isEquationOrComplex = lStep.isComplex || lStep.type === 'equation';
      const countComplexInFrame = currentFrameSteps.filter(s => s.isComplex || s.type === 'equation').length;
      const wouldExceedComplexLimit = isEquationOrComplex && countComplexInFrame >= 2 && currentFrameSteps.length >= 2;

      const wouldExceedStepBudget = currentFrameSteps.length >= frameLimit;
      const wouldExceedHeight = (currentFrameHeight + stepHeight) > effectiveSafeHeight && currentFrameSteps.length >= 1;

      if (currentFrameSteps.length > 0 && (wouldExceedStepBudget || wouldExceedHeight || wouldExceedComplexLimit)) {
        textFramesData.push(currentFrameSteps);
        currentFrameSteps = [lStep];
        currentFrameHeight = stepHeight;
      } else {
        // If a single table step itself is overwhelmingly large
        if (currentFrameSteps.length === 0 && stepHeight > effectiveSafeHeight && lStep.type === 'table' && lStep.lines.length > 3) {
          const headerRow = lStep.lines[0];
          const sepRow = lStep.lines.length > 1 && /^[|\-:\s]+$/.test(lStep.lines[1]) ? lStep.lines[1] : '|---|---|';
          const dataRows = lStep.lines.filter((l, idx) => idx > 1 && !/^[|\-:\s]+$/.test(l));

          const half = Math.ceil(dataRows.length / 2);
          const tableA: LogicalTeachingStep = {
            ...lStep,
            id: `${lStep.id}-part1`,
            lines: [headerRow, sepRow, ...dataRows.slice(0, half)],
            rawText: [headerRow, sepRow, ...dataRows.slice(0, half)].join('\n')
          };
          const tableB: LogicalTeachingStep = {
            ...lStep,
            id: `${lStep.id}-part2`,
            lines: [headerRow, sepRow, ...dataRows.slice(half)],
            rawText: [headerRow, sepRow, ...dataRows.slice(half)].join('\n')
          };
          textFramesData.push([tableA]);
          currentFrameSteps = [tableB];
          currentFrameHeight = measureLogicalStepHeight(tableB, safeBounds.width, baseFontSize);
        } 
        // If a single text/equation logical step is overwhelmingly large
        else if (currentFrameSteps.length === 0 && stepHeight > effectiveSafeHeight && lStep.lines.length > 1) {
          const half = Math.ceil(lStep.lines.length / 2);
          const subStepA: LogicalTeachingStep = {
            ...lStep,
            id: `${lStep.id}-a`,
            lines: lStep.lines.slice(0, half),
            rawText: lStep.lines.slice(0, half).join('\n')
          };
          const subStepB: LogicalTeachingStep = {
            ...lStep,
            id: `${lStep.id}-b`,
            lines: lStep.lines.slice(half),
            rawText: lStep.lines.slice(half).join('\n')
          };
          textFramesData.push([subStepA]);
          currentFrameSteps = [subStepB];
          currentFrameHeight = measureLogicalStepHeight(subStepB, safeBounds.width, baseFontSize);
        } else {
          currentFrameSteps.push(lStep);
          currentFrameHeight += stepHeight;
        }
      }
    }

    if (currentFrameSteps.length > 0) {
      textFramesData.push(currentFrameSteps);
    }
  }

  // Align spoken explanation and captions across text frames using smart semantic matching
  const { frameExplanations, frameAudios } = alignSentencesToTeachingFrames(textFramesData, textExp, textAudio);

  const textFrames: Step[] = textFramesData.map((frameStepUnits, fIdx) => {
    const isLast = fIdx === textFramesData.length - 1;

    return {
      ...step,
      step: stepIndex + fIdx,
      board_content: frameStepUnits.map(u => u.rawText).join('\n'),
      explanation: frameExplanations[fIdx] || textExp,
      audio_explanation: frameAudios[fIdx] || textAudio,
      diagram_svg: undefined, // Strictly no diagram in the text frame
      diagram_description: undefined,
      image_url: undefined,
      static_image_url: undefined,
      reference_text: isLast && !hasDedicatedVisual ? step.reference_text : undefined
    };
  });

  // If a dedicated visual is present, append the DEDICATED VISUAL FRAME right after the text frame(s)
  if (hasDedicatedVisual) {
    const visualFrame: Step = {
      ...step,
      step: stepIndex + textFrames.length,
      board_content: '', // Textual steps from Frame 1 do NOT repeat in Frame 2
      explanation: visualExp,
      audio_explanation: visualAudio,
      diagram_svg: step.diagram_svg,
      diagram_description: step.diagram_description,
      image_url: stepValidImg,
      static_image_url: stepValidImg,
      reference_text: step.reference_text
    };

    return [...textFrames, visualFrame];
  }

  return textFrames;
}

/**
 * Validates and reflows all lesson steps deterministically.
 * Guarantees max 3-4 logical teaching steps per frame, preserving complete logical units and safe boundaries.
 */
export function validateAndPaginateAllSteps(
  steps: Step[],
  targetMode: 'landscape' | 'shorts' | 'player' = 'landscape'
): Step[] {
  if (!Array.isArray(steps) || steps.length === 0) return [];

  const safeBounds = targetMode === 'shorts' ? SAFE_BOUNDS_SHORTS : SAFE_BOUNDS_LANDSCAPE;
  const result: Step[] = [];
  let runningStepNumber = 1;

  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const subSteps = paginateSingleStep(s, runningStepNumber, safeBounds);

    for (const subStep of subSteps) {
      result.push({
        ...subStep,
        step: runningStepNumber++
      });
    }
  }

  return result;
}
